import { useEffect, useState } from 'react';

import { DEBOUNCE_MS } from '@/components/features/UsersList/constants';
import BaseMultiAutocomplete from '@/components/shared/Autocomplete/BaseMultiAutocomplete';
import { BaseMultiAutocompleteProps } from '@/components/shared/Autocomplete/types';
import { useAccountUsersAutocomplete } from '@/hooks/autocomplete/useAccountUsersAutocomplete';
import type { components } from '@/shared/api/openapi';

export type AccountUser = components['schemas']['GetAccountUsersAutocompleteEndpoint_GetUsersResponse'];

export interface AccountUserAutocompleteProps<AccountUser> {
  value: AccountUser[];
  onChange: BaseMultiAutocompleteProps<AccountUser>['autocomplete']['onChange'];
}

function AccountUserAutocomplete({ value, onChange }: AccountUserAutocompleteProps<AccountUser>) {
  const [inputValue, setInputValue] = useState('');
  const [debouncedValue, setDebouncedValue] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(inputValue);
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [inputValue]);

  const { data: users, isLoading } = useAccountUsersAutocomplete({
    filter: debouncedValue,
    enabled: debouncedValue.length >= 3,
  });

  const handleInputChange = (_: React.SyntheticEvent, input: string) => {
    setInputValue(input);
  };

  const getOptionLabel = (option: AccountUser) => {
    return option.email || '';
  };

  const getOptionDisabled = (option: AccountUser) => {
    const isSelectedOptionDisabled = value.some((val) => val.email === option.email);

    return isSelectedOptionDisabled;
  };

  const getOptionKey = (option: AccountUser) => option.id ?? `key-${option.email}`;

  return (
    <BaseMultiAutocomplete<AccountUser>
      autocomplete={{
        options: users || [],
        value: value,
        loading: isLoading,
        onChange: onChange,
        onInputChange: handleInputChange,
        getOptionLabel: getOptionLabel,
        getOptionKey: getOptionKey,
        getOptionDisabled: getOptionDisabled,
        placeholder: 'Enter at least 3 characters to search',
        label: 'Filter by Account Admin Email',
        testIdSuffix: 'emails',
      }}
    />
  );
}

export default AccountUserAutocomplete;
